import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAPI } from './services/api';

interface TgaConfig {
  configured: boolean;
  missing: string[];
  management_mode?: 'direct' | 'asistpro_managed';
  settings_editable?: boolean;
  source?: 'admin' | 'env';
  api_contract_version: string;
  base_url: string | null;
  property_id: string | null;
  has_client_secret?: boolean;
}

interface TgaSettings {
  base_url: string;
  client_id: string;
  client_secret: string;
  property_id: string;
  api_contract_version: string;
  has_client_secret?: boolean;
}

interface TgaStatusResponse {
  success?: boolean;
  property?: {
    id?: number | string;
    name?: string;
  };
  signals?: {
    last_success_at?: string | null;
    lastSuccessfulSyncAt?: string | null;
    hours_since_last_success?: number | null;
    consecutive_failures?: number;
    consecutiveFailureCount?: number;
    last_error_type?: string | null;
    lastErrorType?: string | null;
    last_sync_at?: string | null;
    lastSyncAt?: string | null;
    lastResponseStatus?: number | null;
    lastCorrelationId?: string | null;
    lastErrorMessage?: string | null;
    tga_enabled?: boolean;
    environment?: string;
  };
  error?: string;
}

interface BackfillResponse {
  success?: boolean;
  job?: {
    id?: string;
    status?: string;
    processed_chunks?: number;
    total_chunks?: number;
    last_chunk_date?: string;
    updated_at?: string;
  };
  error?: string;
}

interface TgaLogItem {
  id?: string;
  created_at?: string;
  data_count?: number;
  status?: string;
  correlation_id?: string;
  error_message?: string | null;
}

interface TgaLogsResponse {
  success?: boolean;
  logs?: TgaLogItem[];
  data?: TgaLogItem[];
  items?: TgaLogItem[];
  error?: string;
}

interface TgaAlertItem {
  id?: string;
  alert_type?: string;
  severity?: string;
  message?: string;
  status?: string;
  created_at?: string;
}

interface TgaAlertsResponse {
  success?: boolean;
  alerts?: TgaAlertItem[];
  data?: TgaAlertItem[];
  items?: TgaAlertItem[];
  error?: string;
}

interface ComprehensiveInfo {
  success?: boolean;
  api_contract_version?: string;
  environment?: {
    current_contract_version?: string;
    supported_contract_versions?: string[];
    production_base_url?: string;
    uat_base_url?: string;
    current_environment?: string;
    current?: string;
    asistpro_api_base?: string;
    api_contract_version?: string;
  };
  credentials?: {
    client_id?: string;
    client_secret_masked?: string;
    has_client_secret?: boolean;
    created_at?: string;
    last_rotated_at?: string;
  };
  identity?: {
    client_id_list?: string[];
  };
  properties?: {
    current_property_id?: string | null;
    properties_list: Array<{
      property_id: string;
      property_name: string;
      type: string;
      is_active: boolean;
    }>;
  };
  property_mapping?: {
    selected_property_id?: string;
    selected_property_name?: string;
    all_properties?: Array<{ id?: string; name?: string }>;
  };
  permissions?: {
    scopes?: string[];
    ip_allowlist_enabled?: boolean;
    ip_allowlist?: string[];
    rate_limit_requests_per_minute?: number;
    rate_limit_window_seconds?: number;
  };
  authorization?: {
    required_scopes?: string[];
    granted_scope_union?: string[];
    ip_allowlist_active?: boolean;
  };
  limits_and_behavior?: {
    rate_limit?: string;
    error_code_matrix?: Record<string, string>;
  };
  error_matrix?: Array<{
    code: number;
    meaning: string;
    action: string;
  }>;
  operational?: {
    correlation_id_in_response?: boolean;
    correlation_id_purpose?: string;
    last_successful_sync?: string | null;
    consecutive_failures?: number;
    last_error_type?: string | null;
    backfill_job_statuses?: string[];
    backfill_chunk_processing?: string;
  };
  operational_monitoring?: {
    correlation_id_returned_in_sync_responses?: boolean;
    last_successful_sync_at?: string | null;
    consecutive_failure_count?: number;
    last_error_type?: string | null;
    last_response_status?: number | null;
    last_correlation_id?: string | null;
    backfill_status_counts?: Record<string, number>;
  };
  transition_policy?: {
    current_version: string;
    breaking_change_notice_days: number;
    support_duration_after_deprecation_days: number;
    deprecation_header_name: string;
    sunset_header_name: string;
    changelog_url: string;
    next_planned_version: string | null;
    breaking_changes: any[];
  };
  error?: string;
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '1rem'
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.65rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: '0.95rem'
};

const TgaIntegrationTab: React.FC = () => {
  const [config, setConfig] = useState<TgaConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [status, setStatus] = useState<TgaStatusResponse | null>(null);
  const [retryResponse, setRetryResponse] = useState<any>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [propertyIdInput, setPropertyIdInput] = useState('');
  const [jobId, setJobId] = useState('');
  const [backfill, setBackfill] = useState<BackfillResponse | null>(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [tgaLogsResponse, setTgaLogsResponse] = useState<TgaLogsResponse | null>(null);
  const [tgaAlertsResponse, setTgaAlertsResponse] = useState<TgaAlertsResponse | null>(null);

  const [newApiKey, setNewApiKey] = useState('');
  const [rotatingSecret, setRotatingSecret] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [lastStatusUpdateAt, setLastStatusUpdateAt] = useState<string | null>(null);
  const [retryCooldownSeconds, setRetryCooldownSeconds] = useState(0);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settings, setSettings] = useState<TgaSettings>({
    base_url: '',
    client_id: '',
    client_secret: '',
    property_id: '',
    api_contract_version: '2026-05-07.v1',
    has_client_secret: false
  });
  const [comprehensiveInfo, setComprehensiveInfo] = useState<ComprehensiveInfo | null>(null);
  const [loadingComprehensiveInfo, setLoadingComprehensiveInfo] = useState(true);

  const formatDate = (value?: string | null) => {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('tr-TR');
  };

  const normalizedStatus = useMemo(() => {
    const signals = status?.signals || {};
    return {
      lastSuccess: signals.last_success_at || signals.lastSuccessfulSyncAt || null,
      lastSync: signals.last_sync_at || signals.lastSyncAt || null,
      consecutiveFailures: signals.consecutive_failures ?? signals.consecutiveFailureCount ?? null,
      lastErrorType: signals.last_error_type || signals.lastErrorType || null,
      lastErrorMessage: signals.lastErrorMessage || null,
      lastResponseStatus: signals.lastResponseStatus ?? null,
      correlationId: signals.lastCorrelationId || null,
      environment: signals.environment || null
    };
  }, [status]);

  const normalizedComprehensive = useMemo(() => {
    const env = comprehensiveInfo?.environment || {};
    const statusCounts = comprehensiveInfo?.operational_monitoring?.backfill_status_counts || {};

    const supportedVersions = Array.isArray(env.supported_contract_versions)
      ? env.supported_contract_versions
      : env.api_contract_version
      ? [env.api_contract_version]
      : comprehensiveInfo?.api_contract_version
      ? [comprehensiveInfo.api_contract_version]
      : [];

    const properties = comprehensiveInfo?.properties?.properties_list?.length
      ? comprehensiveInfo.properties.properties_list
      : (comprehensiveInfo?.property_mapping?.all_properties || []).map((p) => ({
          property_id: p.id || '-',
          property_name: p.name || 'Bilinmiyor',
          type: 'hotel',
          is_active: true
        }));

    const permissionScopes = comprehensiveInfo?.permissions?.scopes?.length
      ? comprehensiveInfo.permissions.scopes
      : comprehensiveInfo?.authorization?.granted_scope_union || comprehensiveInfo?.authorization?.required_scopes || [];

    const errorMatrix = Array.isArray(comprehensiveInfo?.error_matrix)
      ? comprehensiveInfo?.error_matrix
      : Object.entries(comprehensiveInfo?.limits_and_behavior?.error_code_matrix || {}).map(([code, meaning]) => ({
          code: Number(code),
          meaning,
          action: '-'
        }));

    return {
      environment: {
        currentContractVersion:
          env.current_contract_version || env.api_contract_version || comprehensiveInfo?.api_contract_version || '-',
        currentEnvironment: env.current_environment || env.current || normalizedStatus.environment || '-',
        baseUrl:
          (env.current_environment === 'production' ? env.production_base_url : env.uat_base_url) ||
          env.asistpro_api_base ||
          '-',
        supportedVersions
      },
      credentials: {
        clientId:
          comprehensiveInfo?.credentials?.client_id ||
          comprehensiveInfo?.identity?.client_id_list?.[0] ||
          '-',
        secretMasked: comprehensiveInfo?.credentials?.client_secret_masked || 'AsistPro tarafinda saklaniyor',
        createdAt: comprehensiveInfo?.credentials?.created_at || null,
        rotatedAt: comprehensiveInfo?.credentials?.last_rotated_at || null
      },
      properties: {
        currentPropertyId:
          comprehensiveInfo?.properties?.current_property_id ||
          comprehensiveInfo?.property_mapping?.selected_property_id ||
          '-',
        properties
      },
      permissions: {
        scopes: permissionScopes,
        ipAllowlistEnabled:
          comprehensiveInfo?.permissions?.ip_allowlist_enabled ??
          comprehensiveInfo?.authorization?.ip_allowlist_active ??
          false,
        ipAllowlist: comprehensiveInfo?.permissions?.ip_allowlist || [],
        rateLimit:
          comprehensiveInfo?.limits_and_behavior?.rate_limit ||
          `${comprehensiveInfo?.permissions?.rate_limit_requests_per_minute ?? '-'} req/${comprehensiveInfo?.permissions?.rate_limit_window_seconds ?? '-'}s`
      },
      errorMatrix: errorMatrix || [],
      operational: {
        correlationInResponse:
          comprehensiveInfo?.operational?.correlation_id_in_response ??
          comprehensiveInfo?.operational_monitoring?.correlation_id_returned_in_sync_responses ??
          false,
        lastSuccessfulSync:
          comprehensiveInfo?.operational?.last_successful_sync ||
          comprehensiveInfo?.operational_monitoring?.last_successful_sync_at ||
          normalizedStatus.lastSuccess ||
          null,
        consecutiveFailures:
          comprehensiveInfo?.operational?.consecutive_failures ??
          comprehensiveInfo?.operational_monitoring?.consecutive_failure_count ??
          normalizedStatus.consecutiveFailures,
        lastErrorType:
          comprehensiveInfo?.operational?.last_error_type ||
          comprehensiveInfo?.operational_monitoring?.last_error_type ||
          normalizedStatus.lastErrorType ||
          null,
        lastResponseStatus:
          comprehensiveInfo?.operational_monitoring?.last_response_status ?? normalizedStatus.lastResponseStatus ?? null,
        lastCorrelationId:
          comprehensiveInfo?.operational_monitoring?.last_correlation_id || normalizedStatus.correlationId || null,
        backfillStatusCounts: statusCounts,
        backfillStatuses:
          comprehensiveInfo?.operational?.backfill_job_statuses ||
          Object.keys(statusCounts)
      }
    };
  }, [comprehensiveInfo, normalizedStatus]);

  const normalizedLogs = useMemo(() => {
    const rows = tgaLogsResponse?.logs || tgaLogsResponse?.data || tgaLogsResponse?.items || [];
    return rows.slice(0, 50);
  }, [tgaLogsResponse]);

  const normalizedAlerts = useMemo(() => {
    return tgaAlertsResponse?.alerts || tgaAlertsResponse?.data || tgaAlertsResponse?.items || [];
  }, [tgaAlertsResponse]);

  const effectivePropertyId = useMemo(() => {
    if (propertyIdInput.trim()) {
      return propertyIdInput.trim();
    }
    return config?.property_id || '';
  }, [propertyIdInput, config?.property_id]);

  const isAsistProManagedMode = config?.management_mode === 'asistpro_managed' || config?.settings_editable === false;

  useEffect(() => {
    if (retryCooldownSeconds <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setRetryCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [retryCooldownSeconds]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4500);
  };

  const loadConfig = useCallback(async () => {
    try {
      setLoadingConfig(true);
      const response = await adminAPI.getTgaConfig();
      const configData: TgaConfig = response.data?.data;
      setConfig(configData);
      const settingsData = response.data?.settings || {};

      setSettings({
        base_url: settingsData.base_url || '',
        client_id: settingsData.client_id || '',
        client_secret: '',
        property_id: settingsData.property_id || '',
        api_contract_version: settingsData.api_contract_version || '2026-05-07.v1',
        has_client_secret: !!settingsData.has_client_secret
      });

      if (configData?.property_id) {
        setPropertyIdInput(configData.property_id);
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'TGA config yüklenemedi');
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const loadStatus = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoadingStatus(true);
      }
      const response = await adminAPI.getTgaStatus(effectivePropertyId || undefined);
      setStatus(response.data as TgaStatusResponse);
      setLastStatusUpdateAt(new Date().toISOString());
    } catch (error: any) {
      setStatus(error.response?.data || { success: false, error: error.message });
    } finally {
      if (!silent) {
        setLoadingStatus(false);
      }
    }
  }, [effectivePropertyId]);

  const loadLogsAndAlerts = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLogsLoading(true);
        setAlertsLoading(true);
      }

      const [logsRes, alertsRes] = await Promise.all([
        adminAPI.getTgaLogs(effectivePropertyId || undefined, 50),
        adminAPI.getTgaAlerts(effectivePropertyId || undefined)
      ]);

      setTgaLogsResponse(logsRes.data as TgaLogsResponse);
      setTgaAlertsResponse(alertsRes.data as TgaAlertsResponse);
    } catch (error: any) {
      if (error?.response?.config?.url?.includes('/tga/logs')) {
        setTgaLogsResponse(error.response?.data || { success: false, error: error.message });
      }
      if (error?.response?.config?.url?.includes('/tga/alerts')) {
        setTgaAlertsResponse(error.response?.data || { success: false, error: error.message });
      }
    } finally {
      if (!silent) {
        setLogsLoading(false);
        setAlertsLoading(false);
      }
    }
  }, [effectivePropertyId]);

  const saveSettings = async () => {
    if (isAsistProManagedMode) {
      showMessage('error', 'Bu ortamda TGA ayarlari AsistPro tarafindan yonetiliyor. Manuel kayit kapali.');
      return;
    }

    if (!settings.base_url.trim()) {
      showMessage('error', 'Base URL zorunludur');
      return;
    }

    if (!settings.client_id.trim()) {
      showMessage('error', 'Client ID zorunludur');
      return;
    }

    try {
      setSettingsLoading(true);
      const response = await adminAPI.updateTgaSettings({
        base_url: settings.base_url.trim(),
        client_id: settings.client_id.trim(),
        client_secret: settings.client_secret.trim(),
        property_id: settings.property_id.trim(),
        api_contract_version: settings.api_contract_version.trim() || '2026-05-07.v1'
      });

      if (response.data?.success) {
        showMessage('success', 'TGA ayarları kaydedildi');
        setSettings((prev) => ({
          ...prev,
          client_secret: '',
          has_client_secret: true
        }));
        await loadConfig();
      } else {
        showMessage('error', response.data?.error || 'Ayar kaydı başarısız');
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.error || 'TGA ayarları kaydedilemedi');
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (!autoRefreshEnabled) {
      return;
    }

    // İlk açılışta durumu hemen çek
    loadStatus(true);

    const poll = setInterval(() => {
      loadStatus(true);
    }, 30000);

    return () => clearInterval(poll);
  }, [autoRefreshEnabled, loadStatus]);

  // Load comprehensive info on mount
  useEffect(() => {
    const loadComprehensiveInfo = async () => {
      try {
        setLoadingComprehensiveInfo(true);
        const response = await adminAPI.getTgaComprehensiveInfo();
        setComprehensiveInfo(response.data);
      } catch (error: any) {
        // Hata durumunda sessizce handle et, 401 interceptor'ını bypass et
        console.warn('TGA comprehensive info yüklenemedi:', error.message);
        setComprehensiveInfo({
          success: false,
          error: 'AsistPro entegrasyonu hazır değil veya bağlantı kurulamadı'
        });
      } finally {
        setLoadingComprehensiveInfo(false);
      }
    };

    loadComprehensiveInfo();
  }, []);

  useEffect(() => {
    if (!effectivePropertyId) {
      return;
    }

    loadLogsAndAlerts(true);
  }, [effectivePropertyId, loadLogsAndAlerts]);

  const triggerRetry = async () => {
    try {
      setRetryLoading(true);
      const response = await adminAPI.retryTga(effectivePropertyId || undefined);
      setRetryResponse(response.data);
      if (response.data?.success) {
        showMessage('success', 'TGA retry işlemi tetiklendi');
      } else {
        showMessage('error', response.data?.error || 'Retry başarısız');
      }
      await loadStatus();
      await loadLogsAndAlerts(true);
    } catch (error: any) {
      const retryAfterSeconds = Number(error.response?.data?.retry_after_seconds);
      if (error.response?.status === 429 && Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
        setRetryCooldownSeconds(Math.floor(retryAfterSeconds));
      }
      const apiError = error.response?.data?.error || error.message;
      showMessage('error', `Retry hatası: ${apiError}`);
      setRetryResponse(error.response?.data || null);
    } finally {
      setRetryLoading(false);
    }
  };

  const loadBackfillStatus = async () => {
    if (!jobId.trim()) {
      showMessage('error', 'Lütfen bir backfill job ID girin');
      return;
    }

    try {
      setBackfillLoading(true);
      const response = await adminAPI.getTgaBackfillStatus(jobId.trim());
      setBackfill(response.data as BackfillResponse);
    } catch (error: any) {
      setBackfill(error.response?.data || { success: false, error: error.message });
    } finally {
      setBackfillLoading(false);
    }
  };

  const backfillAction = async (action: 'pause' | 'resume') => {
    const selectedJobId = backfill?.job?.id || jobId.trim();
    if (!selectedJobId) {
      showMessage('error', 'Önce job ID girin');
      return;
    }

    try {
      setBackfillLoading(true);
      if (action === 'pause') {
        await adminAPI.pauseTgaBackfill(selectedJobId);
      } else {
        await adminAPI.resumeTgaBackfill(selectedJobId);
      }
      showMessage('success', action === 'pause' ? 'Backfill pause gönderildi' : 'Backfill resume gönderildi');
      await loadBackfillStatus();
    } catch (error: any) {
      showMessage('error', error.response?.data?.error || `${action} işlemi başarısız`);
    } finally {
      setBackfillLoading(false);
    }
  };

  const rotateSecret = async () => {
    if (!newApiKey.trim() || newApiKey.trim().length < 8) {
      showMessage('error', 'Yeni API key en az 8 karakter olmalı');
      return;
    }

    try {
      setRotatingSecret(true);
      const response = await adminAPI.rotateTgaSecret(effectivePropertyId || undefined, newApiKey.trim());
      if (response.data?.success) {
        showMessage('success', 'TGA secret başarıyla yenilendi');
        setNewApiKey('');
      } else {
        showMessage('error', response.data?.error || 'Secret rotate başarısız');
      }
    } catch (error: any) {
      showMessage('error', error.response?.data?.error || 'Secret rotate başarısız');
    } finally {
      setRotatingSecret(false);
    }
  };

  return (
    <div className="tab-content">
      <h2>🛰️ TGA Entegrasyon Kontrolü</h2>
      <p style={{ color: '#6b7280' }}>
        AsistPro TGA endpointleri üzerinden canlı sağlık durumu, retry ve backfill işlemlerini yönetin.
      </p>

      {isAsistProManagedMode ? (
        <div style={{ ...cardStyle, borderColor: '#93c5fd', background: '#eff6ff', marginBottom: '1rem' }}>
          <strong>AsistPro-Managed Modu Aktif.</strong> TGA URL ve kimlik bilgileri AsistPro panelinde yonetilir.
          Bu panelde sadece izleme ve operasyon islemleri kullanilir.
        </div>
      ) : (
        <div style={{ ...cardStyle, borderColor: '#fde68a', background: '#fffbeb', marginBottom: '1rem' }}>
          <strong>Direct Mod Aktif.</strong> Simdilik AsistPro'dan aldiginiz TGA bilgilerini bu panelde girmeniz gerekir.
          AsistPro-managed endpointler acildiginda bu alanlar kapatilabilir.
        </div>
      )}

      {message && (
        <div
          style={{
            ...cardStyle,
            borderColor: message.type === 'success' ? '#86efac' : '#fca5a5',
            background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
            marginBottom: '1rem'
          }}
        >
          <strong>{message.type === 'success' ? '✅' : '❌'} </strong>{message.text}
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>🔧 TGA Yapılandırma</h3>

          {isAsistProManagedMode ? (
            <div style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.75rem', marginBottom: '0.75rem' }}>
              Bu bolum bu ortamda kilitli. Yonetim AsistPro panelindedir.
            </div>
          ) : null}

          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem' }}>Base URL</label>
              <input
                value={settings.base_url}
                onChange={(e) => setSettings((prev) => ({ ...prev, base_url: e.target.value }))}
                placeholder="https://..."
                disabled={isAsistProManagedMode}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem' }}>Client ID</label>
              <input
                value={settings.client_id}
                onChange={(e) => setSettings((prev) => ({ ...prev, client_id: e.target.value }))}
                placeholder="svc_xxx"
                disabled={isAsistProManagedMode}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem' }}>Client Secret</label>
              <input
                value={settings.client_secret}
                onChange={(e) => setSettings((prev) => ({ ...prev, client_secret: e.target.value }))}
                placeholder={settings.has_client_secret ? 'Değiştirmek için yeni secret girin' : 'AsistPro secret'}
                disabled={isAsistProManagedMode}
                style={inputStyle}
              />
              <small style={{ color: '#6b7280' }}>
                {settings.has_client_secret ? 'Sistemde kayıtlı secret var. Boş bırakırsanız mevcut secret korunur.' : 'İlk kurulum için zorunlu.'}
              </small>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem' }}>Property ID</label>
              <input
                value={settings.property_id}
                onChange={(e) => {
                  const value = e.target.value;
                  setSettings((prev) => ({ ...prev, property_id: value }));
                  setPropertyIdInput(value);
                }}
                placeholder="42"
                disabled={isAsistProManagedMode}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem' }}>API Contract Version</label>
              <input
                value={settings.api_contract_version}
                onChange={(e) => setSettings((prev) => ({ ...prev, api_contract_version: e.target.value }))}
                placeholder="2026-05-07.v1"
                disabled={isAsistProManagedMode}
                style={inputStyle}
              />
            </div>

            <button onClick={saveSettings} disabled={settingsLoading || isAsistProManagedMode} style={{ padding: '0.6rem 0.9rem' }}>
              {settingsLoading ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
            </button>
          </div>
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>⚙️ Konfigürasyon</h3>
          {loadingConfig ? (
            <p>Yükleniyor...</p>
          ) : (
            <>
              <p><strong>Durum:</strong> {config?.configured ? '✅ Hazır' : '❌ Eksik'}</p>
              <p><strong>Yonetim Modu:</strong> {config?.management_mode === 'asistpro_managed' ? 'AsistPro-Managed' : 'Direct'}</p>
              <p><strong>Kaynak:</strong> {config?.source === 'admin' ? 'Admin Panel' : 'ENV'}</p>
              <p><strong>Base URL:</strong> {config?.base_url || '-'}</p>
              <p><strong>Property ID:</strong> {config?.property_id || '-'}</p>
              <p><strong>Contract:</strong> {config?.api_contract_version || '-'}</p>
              {!config?.configured && config?.missing?.length ? (
                <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: '0.75rem' }}>
                  <strong>Eksik env:</strong> {config.missing.join(', ')}
                </div>
              ) : null}
            </>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>🏨 Tesis Seçimi</h3>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>Property ID</label>
          <input
            value={propertyIdInput}
            onChange={(e) => setPropertyIdInput(e.target.value)}
            placeholder="Örn: 42"
            style={inputStyle}
          />
          <small style={{ color: '#6b7280' }}>
            Boş bırakırsanız backend'deki ASISTPRO_PROPERTY_ID kullanılır.
          </small>

          <div style={{ marginTop: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.92rem' }}>
              <input
                type="checkbox"
                checked={autoRefreshEnabled}
                onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
              />
              Status otomatik yenile (30 saniye)
            </label>
          </div>

          {lastStatusUpdateAt ? (
            <small style={{ color: '#6b7280', display: 'block', marginTop: '0.35rem' }}>
              Son otomatik güncelleme: {new Date(lastStatusUpdateAt).toLocaleString('tr-TR')}
            </small>
          ) : null}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button onClick={() => loadStatus()} disabled={loadingStatus} style={{ padding: '0.6rem 0.9rem' }}>
              {loadingStatus ? 'Yükleniyor...' : 'Durumu Yenile'}
            </button>
            <button onClick={() => loadLogsAndAlerts()} disabled={logsLoading || alertsLoading} style={{ padding: '0.6rem 0.9rem' }}>
              {logsLoading || alertsLoading ? 'Yenileniyor...' : 'Log/Alarm Yenile'}
            </button>
            <button
              onClick={() => triggerRetry()}
              disabled={retryLoading || retryCooldownSeconds > 0}
              style={{ padding: '0.6rem 0.9rem' }}
            >
              {retryLoading
                ? 'Gönderiliyor...'
                : retryCooldownSeconds > 0
                ? `Retry (${retryCooldownSeconds}s)`
                : 'Retry Gönder'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ ...cardStyle, marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>📡 Status</h3>
        {!status ? (
          <p>Henüz durum çekilmedi.</p>
        ) : status.success ? (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <p><strong>Tesis:</strong> {status.property?.name || '-'} (#{status.property?.id || '-'})</p>
            <p><strong>Son başarılı sync:</strong> {formatDate(normalizedStatus.lastSuccess)}</p>
            <p><strong>Son sync:</strong> {formatDate(normalizedStatus.lastSync)}</p>
            <p><strong>Ardışık hata:</strong> {normalizedStatus.consecutiveFailures ?? '-'}</p>
            <p><strong>Son hata tipi:</strong> {normalizedStatus.lastErrorType || '-'}</p>
            <p><strong>Son response status:</strong> {normalizedStatus.lastResponseStatus ?? '-'}</p>
            <p><strong>Correlation ID:</strong> {normalizedStatus.correlationId || '-'}</p>
            <p><strong>Environment:</strong> {normalizedStatus.environment || normalizedComprehensive.environment.currentEnvironment || '-'}</p>
          </div>
        ) : (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.75rem' }}>
            {status.error || 'Status alınamadı'}
          </div>
        )}
      </div>

      <div style={{ ...cardStyle, marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>🔁 Retry Sonucu</h3>
        {!retryResponse ? <p>Henüz retry çağrısı yapılmadı.</p> : (
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem' }}>
            {JSON.stringify(retryResponse, null, 2)}
          </pre>
        )}
      </div>

      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', marginTop: '1rem' }}>
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>📜 Gönderim Geçmişi</h3>
          {tgaLogsResponse?.success === false ? (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.75rem' }}>
              {tgaLogsResponse.error || 'Log verisi alınamadı'}
            </div>
          ) : normalizedLogs.length === 0 ? (
            <p>Henüz gönderim kaydı yok.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '1px solid #d1d5db' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Zaman</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Kayıt</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Correlation</th>
                  </tr>
                </thead>
                <tbody>
                  {normalizedLogs.map((row, idx) => (
                    <tr key={row.id || String(idx)} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.5rem' }}>{formatDate(row.created_at || null)}</td>
                      <td style={{ padding: '0.5rem' }}>{row.status || '-'}</td>
                      <td style={{ padding: '0.5rem' }}>{row.data_count ?? '-'}</td>
                      <td style={{ padding: '0.5rem' }}>{row.correlation_id || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>🚨 Açık Alarmlar</h3>
          {tgaAlertsResponse?.success === false ? (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.75rem' }}>
              {tgaAlertsResponse.error || 'Alarm verisi alınamadı'}
            </div>
          ) : normalizedAlerts.length === 0 ? (
            <p>Açık alarm bulunmuyor.</p>
          ) : (
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              {normalizedAlerts.map((alert, idx) => (
                <div key={alert.id || String(idx)} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.65rem' }}>
                  <p style={{ margin: 0 }}>
                    <strong>{alert.alert_type || 'alert'}</strong> • {alert.severity || '-'} • {alert.status || '-'}
                  </p>
                  <p style={{ margin: '0.35rem 0 0 0' }}>{alert.message || '-'}</p>
                  <small style={{ color: '#6b7280' }}>{formatDate(alert.created_at || null)}</small>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ ...cardStyle, marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>🧩 Backfill Job</h3>
        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
          <input
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            placeholder="Backfill job id"
            style={inputStyle}
          />
          <button onClick={loadBackfillStatus} disabled={backfillLoading}>Durum</button>
          <button onClick={() => backfillAction('pause')} disabled={backfillLoading}>Pause</button>
          <button onClick={() => backfillAction('resume')} disabled={backfillLoading}>Resume</button>
        </div>

        {backfill ? (
          <div style={{ marginTop: '0.85rem' }}>
            {backfill.success ? (
              <>
                <p><strong>Status:</strong> {backfill.job?.status || '-'}</p>
                <p><strong>İlerleme:</strong> {backfill.job?.processed_chunks ?? 0} / {backfill.job?.total_chunks ?? 0}</p>
                <p><strong>Last chunk:</strong> {backfill.job?.last_chunk_date || '-'}</p>
              </>
            ) : (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.75rem' }}>
                {backfill.error || 'Backfill job bulunamadı'}
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div style={{ ...cardStyle, marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>� Kapsamlı Bilgiler</h3>
        {loadingComprehensiveInfo ? (
          <p>Yükleniyor...</p>
        ) : comprehensiveInfo?.error ? (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.75rem' }}>
            {comprehensiveInfo.error}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {/* Environment Info */}
            {comprehensiveInfo?.environment && (
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>🌐 Ortam Bilgileri</h4>
                <div style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                  <p><strong>Güncel Sürüm:</strong> {normalizedComprehensive.environment.currentContractVersion}</p>
                  <p><strong>Ortam:</strong> {normalizedComprehensive.environment.currentEnvironment}</p>
                  <p><strong>Base URL:</strong> {normalizedComprehensive.environment.baseUrl}</p>
                  <p><strong>Desteklenen Sürümler:</strong> {normalizedComprehensive.environment.supportedVersions.join(', ') || '-'}</p>
                </div>
              </div>
            )}

            {/* Credentials */}
            {(comprehensiveInfo?.credentials || comprehensiveInfo?.identity) && (
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>🔑 Kimlik Bilgileri</h4>
                <div style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                  <p><strong>Client ID:</strong> {normalizedComprehensive.credentials.clientId}</p>
                  <p><strong>Secret:</strong> {normalizedComprehensive.credentials.secretMasked}</p>
                  <p><strong>Oluşturulma:</strong> {formatDate(normalizedComprehensive.credentials.createdAt)}</p>
                  <p><strong>Son Rotasyon:</strong> {formatDate(normalizedComprehensive.credentials.rotatedAt)}</p>
                </div>
              </div>
            )}

            {/* Properties */}
            {(comprehensiveInfo?.properties || comprehensiveInfo?.property_mapping) && (
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>🏨 Tesis Bilgileri</h4>
                <div style={{ fontSize: '0.9rem' }}>
                  <p><strong>Güncel Property ID:</strong> {normalizedComprehensive.properties.currentPropertyId || '-'}</p>
                  <p><strong>Kayıtlı Tesisler:</strong></p>
                  <div style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                    {normalizedComprehensive.properties.properties.map((prop, idx) => (
                      <div key={idx} style={{ padding: '0.5rem', background: '#f3f4f6', borderRadius: 4, marginBottom: '0.25rem' }}>
                        <strong>{prop.property_name}</strong> (ID: {prop.property_id}) • {prop.type} • 
                        <span style={{ color: prop.is_active ? '#16a34a' : '#dc2626' }}>
                          {prop.is_active ? ' ✅ Aktif' : ' ❌ Pasif'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Permissions */}
            {(comprehensiveInfo?.permissions || comprehensiveInfo?.authorization || comprehensiveInfo?.limits_and_behavior) && (
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>🔐 Yetki & Limitler</h4>
                <div style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                  <p><strong>Scopeler:</strong> {normalizedComprehensive.permissions.scopes.join(', ') || '-'}</p>
                  <p><strong>Rate Limit:</strong> {normalizedComprehensive.permissions.rateLimit}</p>
                  <p><strong>IP Allowlist:</strong> {normalizedComprehensive.permissions.ipAllowlistEnabled ? 'Aktif' : 'Pasif'}</p>
                  {normalizedComprehensive.permissions.ipAllowlistEnabled && normalizedComprehensive.permissions.ipAllowlist.length > 0 && (
                    <p><strong>İzin Verilen IPs:</strong> {normalizedComprehensive.permissions.ipAllowlist.join(', ')}</p>
                  )}
                </div>
              </div>
            )}

            {/* Error Matrix */}
            {(normalizedComprehensive.errorMatrix ?? []).length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>❌ Hata Kod Matrisi</h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#f3f4f6', borderBottom: '1px solid #d1d5db' }}>
                        <th style={{ padding: '0.5rem', textAlign: 'left' }}>Kod</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left' }}>Anlam</th>
                        <th style={{ padding: '0.5rem', textAlign: 'left' }}>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(normalizedComprehensive.errorMatrix ?? []).map((err, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '0.5rem', fontWeight: 'bold' }}>{err.code}</td>
                          <td style={{ padding: '0.5rem' }}>{err.meaning}</td>
                          <td style={{ padding: '0.5rem' }}>{err.action}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Operational Info */}
            {(comprehensiveInfo?.operational || comprehensiveInfo?.operational_monitoring) && (
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>⚡ Operasyonel Bilgiler</h4>
                <div style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                  <p><strong>Correlation ID:</strong> {normalizedComprehensive.operational.correlationInResponse ? '✅ Yanıtta var' : '❌ Yok'}</p>
                  <p><strong>Son Başarılı Sync:</strong> {formatDate(normalizedComprehensive.operational.lastSuccessfulSync)}</p>
                  <p><strong>Ardışık Hata:</strong> {normalizedComprehensive.operational.consecutiveFailures ?? '-'}</p>
                  <p><strong>Son Hata Tipi:</strong> {normalizedComprehensive.operational.lastErrorType || '-'}</p>
                  <p><strong>Son Response Status:</strong> {normalizedComprehensive.operational.lastResponseStatus ?? '-'}</p>
                  <p><strong>Son Correlation ID:</strong> {normalizedComprehensive.operational.lastCorrelationId || '-'}</p>
                  <p><strong>Backfill Durumları:</strong> {normalizedComprehensive.operational.backfillStatuses.join(', ') || '-'}</p>
                  <p><strong>Backfill Sayaçları:</strong> {Object.keys(normalizedComprehensive.operational.backfillStatusCounts).length
                    ? Object.entries(normalizedComprehensive.operational.backfillStatusCounts).map(([k, v]) => `${k}: ${v}`).join(', ')
                    : '-'}</p>
                </div>
              </div>
            )}

            {/* Transition Policy */}
            {comprehensiveInfo?.transition_policy && (
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>📅 Geçiş Politikası</h4>
                <div style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                  <p><strong>Güncel Sürüm:</strong> {comprehensiveInfo.transition_policy.current_version}</p>
                  <p><strong>Duyuru Süresi:</strong> {comprehensiveInfo.transition_policy.breaking_change_notice_days} gün</p>
                  <p><strong>Destek Süresi:</strong> {comprehensiveInfo.transition_policy.support_duration_after_deprecation_days} gün (deprecation sonrası)</p>
                  <p><strong>Sonraki Sürüm:</strong> {comprehensiveInfo.transition_policy.next_planned_version || 'Planlanmamış'}</p>
                  <p><strong>Changelog:</strong> <a href={comprehensiveInfo.transition_policy.changelog_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc' }}>{comprehensiveInfo.transition_policy.changelog_url}</a></p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ ...cardStyle, marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>�🔐 Secret Rotation</h3>
        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '3fr 1fr' }}>
          <input
            value={newApiKey}
            onChange={(e) => setNewApiKey(e.target.value)}
            placeholder="Yeni TGA API key"
            style={inputStyle}
          />
          <button onClick={rotateSecret} disabled={rotatingSecret}>
            {rotatingSecret ? 'Dönüyor...' : 'Rotate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TgaIntegrationTab;
